"use client";
import React, { useState } from "react";
import { Play, ExternalLink, X } from "lucide-react";
import Image from "next/image";

const VideoCard = ({ videoId, title, caption, thumbnailUrl }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);

    const handleCardClick = (event) => {
        if (event.target.closest("[data-external-link]")) return;
        setShowVideoModal(true);
    };

    const handleExternalLinkClick = (event) => {
        event.stopPropagation();
        window.open(`https://www.youtube.com/watch?v=${videoId}`, "_blank");
    };

    const handleCloseModal = () => setShowVideoModal(false);

    return (
        <>
            <div
                className="group cursor-pointer"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleCardClick}
            >
                <div className="relative overflow-hidden bg-white/70 backdrop-blur-md border border-white/60 shadow-lg shadow-slate-200/50 transition-all duration-500 ease-out hover:shadow-2xl hover:shadow-slate-300/50 hover:-translate-y-1">
                    {/* THUMBNAIL BLOCK */}
                    <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
                        {!imageError ? (
                            <Image
                                src={thumbnailUrl}
                                alt={title}
                                fill
                                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                className="object-cover transition-transform duration-700 ease-out group-hover:scale-105 z-0"
                                onError={() => setImageError(true)}
                                loading="lazy"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-blue-100 z-0">
                                {/* <Play className="size-16 text-purple-400" /> */}
                                <Image
                                    src="/play.svg"
                                    alt="Play"
                                    width={18}
                                    height={22}
                                    className="ml-1.5 hover:ml-1.5"
                                />
                            </div>
                        )}

                        {/* hover tint – sits ABOVE image but is transparent */}
                        <div className="absolute inset-0 bg-[#FF8B28]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" />

                        {/* play button – top layer */}
                        <div className="absolute inset-0 flex items-center justify-center transition-all duration-500 z-20">
                            <div className="size-16 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-2xl transform group-hover:scale-105 transition-transform duration-500">
                                {/* <Play className="size-7 text-[#FF8B28] ml-1" fill="currentColor" /> */}
                                <Image
                                    src="/play.svg"
                                    alt="Play"
                                    width={18}
                                    height={22}
                                    className="ml-1.5 hover:ml-1.5"
                                />
                            </div>
                        </div>

                        {/* external link button */}
                        <button
                            type="button"
                            data-external-link
                            onClick={handleExternalLinkClick}
                            className={`absolute top-4 right-4 size-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg transition-all duration-300 z-30 ${isHovered ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
                                }`}
                        >
                            <ExternalLink className="size-4 text-slate-700" />
                        </button>
                    </div>

                    {/* TEXT */}
                    <div className="p-6">
                        <h3
                            className="line-clamp-2 min-h-[3rem] leading-snug group-hover:text-[#FF8B28] transition-colors duration-300"
                            style={{ fontFamily: "saans-bold" }}
                        >
                            {title}
                        </h3>
                    </div>

                    {/* bottom bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#FF8B28] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
            </div>

            {/* MODAL */}
            {showVideoModal && (
                <div
                    className="fixed inset-0 z-999 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
                    onClick={handleCloseModal}
                >
                    <div
                        className="relative w-full max-w-4xl h-auto aspect-video  shadow-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <iframe
                            className="w-full h-full border-0"
                            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                            title={title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                        />
                        <button
                            onClick={handleCloseModal}
                            className="absolute top-4 right-4 cursor-pointer z-10 md:size-10 size-3 rounded-full bg-white/90 text-slate-800 flex items-center justify-center shadow-xl transition hover:bg-white/100"
                            aria-label="Close video player"
                        >
                            <X className="md:size-5 size-2" />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default VideoCard;
